// Drive folders: the rules, shared by the page, the server code (functions/src/drive.ts) and the unit tests.
// Words: SITE_FOLDERS_WORDS.md rev 3.1 (approved 2026-10-22). Mockup: MOCKUP_DRIVE_FOLDERS.html rev 2 (approved 2026-10-22).
// Pure: no database, no network.
//
// Drive is Robert's file cabinet. The app writes into it and never renames, moves or deletes.
// The app stores folder IDs, never paths, so pointing a customer somewhere else is a setting (section 5).

// ---- The folder template ----

/** Under the top folder Robert picks (Active Solutions Master). Customers sit here. */
export const CUSTOMERS_FOLDER = "Customers";

/** The sheet in Customers with one row per site, every customer (section 10). */
export const SITE_INDEX_NAME = "Site Index";
export const SITE_INDEX_HEADERS = ["Customer", "Site #", "Site Name", "Brand", "Folder", "Status", "Folder Built", "Notes"] as const;

/** Inside each customer folder, made with the customer's first site (section 11). */
export const CUSTOMER_FOLDERS: readonly FolderSpec[] = [{ key: "customer", name: "00 Customer", children: [{ key: "programs", name: "Programs" }] }];

export interface FolderSpec {
  key: string;
  name: string;
  children?: readonly FolderSpec[];
}

export type SiteFolderKey = "fieldLabels" | "fieldPhotos" | "assetIds" | "qrLabels" | "bagTags" | "survey" | "afStudy" | "afLabels" | "verification" | "siteInfo" | "customerDocs";

/**
 * Inside each site folder. The three label kinds sit together (02 to 03, 06). Numbers 10 and up are held
 * for LOTO and CMMS. Numbers never change once a site has files in them (rev 3.2, 2026-10-22).
 * 00 Field Labels added 2026-10-23 (Robert, option A): Step 0 of a site, sorts first, nothing else moves.
 */
export const SITE_FOLDERS: readonly (FolderSpec & { key: SiteFolderKey })[] = [
  // Photos: room and gear photos from Field Labels, inside the step that took them (ROOM_GEAR_PHOTO_B5_WORDS.md,
  // Robert 2026-10-28 5:19 PM). The step numbers stay as they are.
  { key: "fieldLabels", name: "00 Field Labels", children: [{ key: "fieldPhotos", name: "Photos" }] },
  { key: "assetIds", name: "01 Asset IDs" },
  { key: "qrLabels", name: "02 QR Labels" },
  { key: "bagTags", name: "03 QEW Tags" },
  { key: "survey", name: "04 Survey" },
  { key: "afStudy", name: "05 Arc Flash Study" },
  { key: "afLabels", name: "06 Arc Flash Labels" },
  { key: "verification", name: "07 Verification" },
  { key: "siteInfo", name: "08 Site Info" },
  { key: "customerDocs", name: "09 Customer Documents" },
];

/** Every folder in a site folder, a folder inside another included, with its names from the site folder down. */
export function siteFolderPaths(specs: readonly FolderSpec[] = SITE_FOLDERS, above: readonly string[] = []): { key: SiteFolderKey; names: string[] }[] {
  return specs.flatMap((s) => {
    const names = [...above, s.name];
    return [{ key: s.key as SiteFolderKey, names }, ...(s.children?.length ? siteFolderPaths(s.children, names) : [])];
  });
}

/** `10 Baconton`. The site number keeps its two digits so folders sort in order. */
export function siteFolderName(siteNumber: string, siteName: string): string {
  return `${siteNumber} ${cleanName(siteName)}`;
}

/** A folder or file name Drive and every PC can hold: no slashes or control characters, trimmed, one space between words. */
export function cleanName(v: string): string {
  return v
    .replace(/[\u0000-\u001f\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

/** Adopting a folder already there: the same name, ignoring case and extra spaces. */
export function sameName(a: string, b: string): boolean {
  return cleanName(a).toLowerCase() === cleanName(b).toLowerCase();
}

// ---- What the app drops in (section 12) ----

export type DriveFileKind = "qr" | "af" | "fl" | "tags" | "photo";

export const FILE_KINDS: Record<DriveFileKind, { folder: SiteFolderKey; word: string }> = {
  qr: { folder: "qrLabels", word: "QR_Labels" },
  af: { folder: "afLabels", word: "AF_Labels" },
  fl: { folder: "fieldLabels", word: "Field_Labels" },
  // QEW tags, saved by Add Site (ADD_SITE_WORDS.md) and Print QEW Tags. Into 03 QEW Tags.
  tags: { folder: "bagTags", word: "QEW_Tags" },
  // Room and gear photos from Field Labels. Into 00 Field Labels / Photos. Named by room and gear, not by a word.
  photo: { folder: "fieldPhotos", word: "" },
};

export function isFileKind(v: unknown): v is DriveFileKind {
  return v === "qr" || v === "af" || v === "fl" || v === "tags" || v === "photo";
}

// ---- Room and gear photos (ROOM_GEAR_PHOTO_B5_WORDS.md, mockup approved 2026-10-29 5:19 AM) ----
// Drive holds one JPEG per room and per gear, named from the walk at send time: 00_ER2.jpg, 00_ER2_XFMR-01.jpg.
// Like a master, each send gets its own outbox name (two underscores and a stamp), and the server replaces the
// Drive file in place. A re-code or a rename sends again under the new name and tells the server the old one (was).

/** A room or gear name as Drive takes it: letters, numbers and dashes. */
const namePart = (v: string, max: number) => v.replace(/[^A-Za-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, max);

/** `00_ER2.jpg`, or `00_ER2_XFMR-01.jpg` for a gear. */
export function photoDriveName(siteNumber: string, room: string, gear = ""): string {
  const r = namePart(room, 12).replace(/-/g, "") || "Room";
  const g = gear ? namePart(gear, 40) : "";
  return `${siteNumber}_${r}${g ? `_${g}` : ""}.jpg`;
}

const PHOTO = /^(\d{2}_[A-Za-z0-9]{1,12}(_[A-Za-z0-9-]{1,40})?)\.jpg$/;
const PHOTO_SEND = /^(\d{2}_[A-Za-z0-9]{1,12}(_[A-Za-z0-9-]{1,40})?)__(\d{13})\.jpg$/;

/** The outbox name for one send of a photo: `00_ER2__1761400000000.jpg`. */
export function photoSendName(driveName: string, ms: number): string {
  if (!PHOTO.test(driveName)) throw new Error(`Not a photo name: ${driveName}`);
  return driveName.replace(/\.jpg$/, `__${String(Math.max(0, Math.floor(ms))).padStart(13, "0").slice(-13)}.jpg`);
}

/** A photo send's Drive name and stamp. Null when the name is not one. */
export function photoOfSend(v: string): { driveName: string; stamp: number } | null {
  const m = PHOTO_SEND.exec(v);
  return m ? { driveName: `${m[1]}.jpg`, stamp: Number(m[3]) } : null;
}

/** A Drive name the app gives a photo. The server only renames a file with one of these. */
export function isPhotoDriveName(v: string): boolean {
  return PHOTO.test(v);
}

/** `10_QR_Labels_2026-10-23_ER1`. The scope is a room or range, letters, numbers and dashes only. */
export function labelFileBase(kind: DriveFileKind, siteNumber: string, ymd: string, scope = ""): string {
  const s = scope.replace(/[^A-Za-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  return `${siteNumber}_${FILE_KINDS[kind].word}_${ymd}${s ? `_${s}` : ""}`;
}

/** The first free name: base.pdf, then base_2.pdf, base_3.pdf. Nothing is overwritten. */
export function freeName(base: string, ext: string, taken: Iterable<string>): string {
  const have = new Set([...taken].map((t) => t.toLowerCase()));
  let name = `${base}.${ext}`;
  for (let n = 2; have.has(name.toLowerCase()); n++) name = `${base}_${n}.${ext}`;
  return name;
}

/** The Storage path a label PDF waits at until the server copies it to Drive. */
export function outboxPath(company: string, siteNumber: string, kind: DriveFileKind, fileName: string): string {
  return `companies/${company}/sites/${siteNumber}/driveOutbox/${kind}/${fileName}`;
}

export interface OutboxPathParts {
  company: string;
  siteNumber: string;
  kind: DriveFileKind;
  fileName: string;
}

/**
 * Reads an outbox path back. Null when the path is not one of ours.
 * Takes every kind in FILE_KINDS, so a new label kind can't be dropped here by a missed list
 * (QEW tags were, 2026-10-24). The file name must belong to the folder it sits in.
 */
export function parseOutboxPath(path: string): OutboxPathParts | null {
  const m = /^companies\/([^/]+)\/sites\/(\d{2})\/driveOutbox\/([a-z]+)\/([^/]+\.(pdf|jpg))$/.exec(path);
  if (!m) return null;
  const kind = m[3] ?? "";
  const fileName = m[4] ?? "";
  if (!isFileKind(kind) || !(isLabelFileName(fileName) || photoOfSend(fileName)) || kindOfFileName(fileName) !== kind) return null;
  if (photoOfSend(fileName) && !fileName.startsWith(`${m[2]}_`)) return null;
  return { company: m[1] ?? "", siteNumber: m[2] ?? "", kind, fileName };
}

/** A file name the page may upload: the base the app builds plus .pdf, dated (before list 2) or a master update. */
export function isLabelFileName(v: string): boolean {
  return /^\d{2}_((QR|AF|Field)_Labels|QEW_Tags)_\d{4}-\d{2}-\d{2}(_[A-Za-z0-9-]{1,40})?(_\d{1,3})?\.pdf$/.test(v) || isMasterSendName(v);
}

// ---- One PDF per label type (COHESION_CHANGE_LOG.md list 2 item 2, approved 2026-10-25, MOCKUP_ONE_PRINT_REV5.html) ----
// Drive holds one PDF per roll, always current: same name and link forever, no date, no _2.
//   10_QR_Labels.pdf, 10_QEW_Tags.pdf, 10_Field_Labels_1x2.pdf (one per size), 10_AF_Labels_WARNING.pdf and _DANGER.pdf.
// Each Update PDF sends the whole file. The outbox can't overwrite (storage rule 7), so each send gets its
// own name: the Drive name, two underscores, the time in milliseconds. The server drops the stamp and
// replaces the file's contents in place. A newer stamp never loses to an older one.

/** "1x2", "0p5x1p5": a field label size in a Drive name. Height first, then length, as the size reads. */
export function sizePart(height: number, length: number): string {
  const n = (v: number) => String(v).replace(".", "p");
  return `${n(height)}x${n(length)}`;
}

/** The one Drive name for a roll. part: the field size ("1x2") or the arc flash stock ("WARNING"). */
export function masterName(kind: DriveFileKind, siteNumber: string, part = ""): string {
  const base = `${siteNumber}_${FILE_KINDS[kind].word}`;
  return kind === "fl" || kind === "af" ? `${base}_${part}.pdf` : `${base}.pdf`;
}

const MASTER = /^(\d{2}_(QR_Labels|QEW_Tags|Field_Labels_[0-9p]{1,4}x[0-9p]{1,4}|AF_Labels_(WARNING|DANGER)))\.pdf$/;
const MASTER_SEND = /^(\d{2}_(QR_Labels|QEW_Tags|Field_Labels_[0-9p]{1,4}x[0-9p]{1,4}|AF_Labels_(WARNING|DANGER)))__(\d{13})\.pdf$/;

export function isMasterName(v: string): boolean {
  return MASTER.test(v);
}

/** The outbox name for one send of a master: `10_QR_Labels__1761400000000.pdf`. */
export function masterSendName(master: string, ms: number): string {
  if (!isMasterName(master)) throw new Error(`Not a label PDF name: ${master}`);
  return master.replace(/\.pdf$/, `__${String(Math.max(0, Math.floor(ms))).padStart(13, "0").slice(-13)}.pdf`);
}

export function isMasterSendName(v: string): boolean {
  return MASTER_SEND.test(v);
}

/** A send's Drive name and stamp. Null for a dated name. */
export function masterOfSend(v: string): { driveName: string; stamp: number } | null {
  const m = MASTER_SEND.exec(v);
  return m ? { driveName: `${m[1]}.pdf`, stamp: Number(m[4]) } : null;
}

/** The DriveFileKind a file name the app made belongs to. */
export function kindOfFileName(v: string): DriveFileKind {
  if (photoOfSend(v)) return "photo";
  return v.includes("_AF_Labels_") ? "af" : v.includes("_Field_Labels_") ? "fl" : v.includes("_QEW_Tags_") ? "tags" : "qr";
}

// ---- What Firestore holds ----

/** companies/{company}/settings/drive. Server writes. Admins read. The sign-in grant itself is never here. */
export interface DriveSettings {
  connected: boolean;
  /** The Google account Robert signed in with. */
  account: string;
  /** The top folder: Active Solutions Master. Customers is found or made inside it. */
  rootFolderId: string;
  rootFolderName: string;
  customersFolderId: string;
  siteIndexId: string;
  /** One folder per customer under Customers, by company. */
  customerFolderId: string;
  changedAt: string;
  changedByName: string;
}

/** On the site record: companies/{company}/sites/{site}.driveFolder. Server writes. */
export interface SiteDriveFolder {
  id: string;
  url: string;
  name: string;
  builtAt: string;
  builtByName: string;
  folders: Partial<Record<SiteFolderKey, string>>;
}

/** companies/{company}/sites/{site}/driveRequests/{id}. The page asks; the server builds. */
export interface DriveRequest {
  kind: "folder";
  at: string;
  by: string;
  byName: string;
  state?: "done" | "failed";
  reason?: string;
  doneAt?: string;
}

/** companies/{company}/sites/{site}/driveFiles/{fileName}. One per label PDF the server copied to Drive. */
export interface DriveFileRecord {
  kind: DriveFileKind;
  /** The name the page gave it. */
  sentName: string;
  /** The name in Drive. A dated name differs only when it was taken (_2). A master send drops its stamp. */
  driveName: string;
  /** A master send: the file was made or replaced in place under driveName. */
  master?: boolean;
  /** A master send that arrived after a newer one. Drive kept the newer file. */
  older?: boolean;
  /** A photo sent under a new name: the Drive file it renamed (a re-code, a gear rename). */
  renamedFrom?: string;
  driveFileId: string;
  url: string;
  folderPath: string;
  savedAt: string;
  byName: string;
  state: "saved" | "failed" | "retrying";
  reason?: string;
}

/** The folder link Drive opens. */
export function folderUrl(id: string): string {
  return `https://drive.google.com/drive/folders/${id}`;
}

/** Alltech › 10 Baconton › 02 QR Labels: the words on the Saved to Drive line. */
export function folderPathWords(customer: string, siteFolder: string, sub: string): string {
  return [customer, siteFolder, sub].filter(Boolean).join(" › ");
}

/** A folder link or a bare ID, as pasted into Change. Null when it is neither. */
export function folderIdFrom(v: string): string | null {
  const t = v.trim();
  const m = /\/folders\/([A-Za-z0-9_-]{10,})/.exec(t) ?? /[?&]id=([A-Za-z0-9_-]{10,})/.exec(t);
  if (m?.[1]) return m[1];
  return /^[A-Za-z0-9_-]{10,}$/.test(t) ? t : null;
}

/** The Site Index row for one site. Notes are Robert's: the app never writes that column. */
export function siteIndexRow(v: { customer: string; siteNumber: string; siteName: string; brand: string; folderId: string; isActive: boolean; builtOn: string }): string[] {
  return [v.customer, v.siteNumber, v.siteName, v.brand, folderUrl(v.folderId), v.isActive ? "Active" : "Not Active", v.builtOn];
}

/** Where a site's row sits in the Site Index values: matched on Customer and Site #. -1 when it has none. */
export function siteIndexRowOf(values: readonly (readonly unknown[])[], customer: string, siteNumber: string): number {
  return values.findIndex((r, i) => i > 0 && sameName(String(r[0] ?? ""), customer) && String(r[1] ?? "").padStart(2, "0") === siteNumber);
}

// ---- Where the app's Drive sign-in may send the browser back to ----

/** The app's own addresses only. Anything else is refused, so a link can't borrow the sign-in. */
export function isAppOrigin(v: string): boolean {
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return false;
  }
  if (u.origin !== v.replace(/\/+$/, "")) return false;
  if (u.protocol === "http:" && (u.hostname === "localhost" || u.hostname === "127.0.0.1")) return true;
  if (u.protocol !== "https:") return false;
  return /^getsafetag-(dev|prod)(-[a-z0-9]+)?\.(web\.app|firebaseapp\.com)$/.test(u.hostname) || /(^|\.)getsafetag\.com$/.test(u.hostname);
}

/** The redirect address Google sends the sign-in back to. Robert enters this in the OAuth client. */
export function connectDoneUrl(projectId: string): string {
  return `https://us-central1-${projectId}.cloudfunctions.net/driveConnectDone`;
}

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

/**
 * The page Google's sign-in lands on when Connect Drive started in the iPad Home Screen app.
 * iPadOS shows Google inside a small window over the app. Sending that window back into SafeTag
 * opened a second copy of the app that hung on "Loading program settings…" (2026-10-22).
 * This page never loads the app. Closing the window shows the app underneath, which re-reads Drive.
 */
export function connectDonePage(result: "connected" | "failed", why = ""): string {
  const ok = result === "connected";
  const title = ok ? "Google Drive is connected" : "Google Drive did not connect";
  const body = ok
    ? "SafeTag can now save site folders and label PDFs to Drive."
    : `${escHtml(why || "Google did not finish the sign-in.")} Go back and tap Connect Drive again.`;
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>SafeTag · Google Drive</title>
<style>body{margin:0;font:17px/1.45 -apple-system,system-ui,sans-serif;background:#1c1c1e;color:#f2f2f7;display:flex;min-height:100vh;align-items:center;justify-content:center}
main{max-width:520px;padding:32px 20px}h1{font-size:24px;margin:0 0 12px;color:${ok ? "#34c759" : "#ff6b5e"}}p{margin:0 0 14px}
.go{margin-top:22px;padding:14px 16px;border:1px solid #f5c542;border-radius:10px;color:#f5c542;font-weight:600}</style></head>
<body><main><h1>${title}</h1><p>${body}</p>
<p class="go">Tap the X (or Done) at the top left of this window to go back to SafeTag.</p></main></body></html>`;
}

/**
 * Words for a refusal from Google, with Google's own answer on the end so a stuck connect says what went wrong
 * (2026-10-22: every 401 read "expired or removed", which hid the real cause).
 * step "sign-in": the one-time trade of Google's code for a lasting sign-in, during Connect Drive.
 * step "refresh": the daily trade of the lasting sign-in for a working key, during Build Folder and saves.
 */
export function googleErrorWords(step: "sign-in" | "refresh" | "drive", status: number, code: string, detail = ""): string {
  const said = ` Google said: ${code || `HTTP ${status}`}${detail ? `, ${detail}` : ""} (HTTP ${status}).`;
  if (code === "invalid_client") return `Google did not accept the app's Drive sign-in settings. The Client ID or client secret in Firebase does not match the SafeTag Drive client.${said}`;
  if (step === "sign-in" && (code === "invalid_grant" || status === 401)) return `Google would not finish this sign-in. Tap Connect Drive again.${said}`;
  if (status === 401 || code === "invalid_grant") return `Google Drive sign-in has expired or was removed. Connect Drive again in Program Settings.${said}`;
  if (status === 404) return `A Drive folder the app saved is gone or no longer shared. Tap Build Folder to find or make it again.${said}`;
  return `Google Drive said: ${code || `HTTP ${status}`}${detail ? `, ${detail}` : ""}.`;
}

/**
 * How often the Creator re-reads the Drive connection. It used to read once per app open, so after
 * Connect Drive the pages kept saying "not connected" until the app was closed (2026-10-22).
 */
export const DRIVE_REREAD_MS = 15_000;

/** True when the last read of the Drive connection is old enough to read again. */
export function driveReadDue(lastReadAt: number, now: number): boolean {
  return now - lastReadAt >= DRIVE_REREAD_MS;
}

// ---- The standard workbook (ADD_SITE_WORDS.md, approved 2026-10-23) ----

/** Where the standard workbook lives, under the top folder: SafeTag Platform › _Templates. */
export const TEMPLATE_PATH: readonly string[] = ["SafeTag Platform", "_Templates"];
export const WORKBOOK_TEMPLATE_NAME = "Field Asset ID Creator.xlsx";
export const NO_TEMPLATE = "Put the standard workbook in SafeTag Platform › _Templates.";

/** The site's copy in 01 Asset IDs. A straight copy: no cell is changed. */
export function workbookCopyName(siteNumber: string): string {
  return `${siteNumber} ${WORKBOOK_TEMPLATE_NAME}`;
}
